import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AvatarDisplay, { AVATAR_OPTIONS } from '../components/AvatarDisplay';

/* ─── Shared Inline Styles ─── */
const PAGE = {
  height: '100%', width: '100%', background: 'var(--bgPrimary)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
  fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
};

const HEADER = {
  flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0 32px', height: 56, borderBottom: '1px solid var(--borderSecondary)',
  background: 'var(--bgSecondary)',
};

const BODY = {
  flex: 1, overflow: 'auto', display: 'flex',
};

function ProfilePage({ isMobileLayout = false }) {
  const {
    user: currentUser, updateProfile, changePassword,
    uploadProfilePhoto, removeProfilePhoto, logout,
  } = useAuth();

  const mob = isMobileLayout;
  const [activeTab, setActiveTab] = useState('details');
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editAvatar, setEditAvatar] = useState('pilot-1');
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (!currentUser) return;
    setEditName(currentUser.name || '');
    setEditEmail(currentUser.email || '');
    setEditAvatar(currentUser.avatar || 'pilot-1');
  }, [currentUser]);

  const memberSince = useMemo(() => {
    if (!currentUser?.createdAt) return 'N/A';
    return new Date(currentUser.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  }, [currentUser?.createdAt]);

  /* ─── Handlers ─── */
  const handleProfileSave = async () => {
    setProfileSaving(true); setProfileMsg(null);
    const result = await updateProfile({ name: editName, email: editEmail, avatar: editAvatar });
    setProfileSaving(false);
    if (result.success) {
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
      setTimeout(() => setActiveTab('details'), 1200);
    } else {
      setProfileMsg({ type: 'error', text: result.message || 'Update failed' });
    }
  };

  const handlePasswordChange = async () => {
    setPwdMsg(null);
    if (newPwd.length < 6) { setPwdMsg({ type: 'error', text: 'Minimum 6 characters' }); return; }
    if (newPwd !== confirmPwd) { setPwdMsg({ type: 'error', text: 'Passwords do not match' }); return; }
    setPwdSaving(true);
    const result = await changePassword(currentPwd, newPwd);
    setPwdSaving(false);
    if (result.success) {
      setPwdMsg({ type: 'success', text: 'Password changed.' });
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
      setTimeout(() => setActiveTab('details'), 1200);
    } else {
      setPwdMsg({ type: 'error', text: result.message || 'Failed' });
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!['image/jpeg','image/jpg','image/png','image/gif','image/webp'].includes(file.type)) { setProfileMsg({ type: 'error', text: 'Only JPG, PNG, GIF, WebP allowed' }); return; }
    if (file.size > 5 * 1024 * 1024) { setProfileMsg({ type: 'error', text: 'Max 5 MB' }); return; }
    setPhotoUploading(true); setProfileMsg(null);
    const result = await uploadProfilePhoto(file);
    setPhotoUploading(false);
    if (result.success) {
      setProfileMsg({ type: 'success', text: 'Photo uploaded.' });
    } else {
      setProfileMsg({ type: 'error', text: result.message || 'Upload failed' });
    }
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleRemovePhoto = async () => {
    setPhotoUploading(true); setProfileMsg(null);
    const result = await removeProfilePhoto();
    setPhotoUploading(false);
    if (result.success) {
      setProfileMsg({ type: 'success', text: 'Photo removed' });
      setEditAvatar('pilot-1');
    } else {
      setProfileMsg({ type: 'error', text: result.message || 'Failed' });
    }
  };

  /* ─── Sub-components ─── */
  const Toast = ({ msg }) => msg ? (
    <div style={{
      padding: '10px 16px', borderRadius: 6, fontSize: 13, fontWeight: 500,
      background: msg.type === 'success' ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.1)',
      borderLeft: `3px solid ${msg.type === 'success' ? '#10b981' : '#ef4444'}`,
      color: msg.type === 'success' ? '#10b981' : '#f87171', marginBottom: 16,
    }}>{msg.text}</div>
  ) : null;

  const tabs = [
    { key: 'details', label: 'Overview', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { key: 'edit', label: 'Edit Profile', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
    { key: 'password', label: 'Security', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
  ];

  /* ─── Shared styles ─── */
  const navBtn = (active) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: mob ? '8px 12px' : '10px 14px', borderRadius: 8, cursor: 'pointer',
    fontSize: 13, fontWeight: 500, width: '100%', textAlign: 'left',
    border: 'none', transition: 'all .15s',
    background: active ? 'rgba(34,211,238,.06)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--textMuted)',
    borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
  });

  const fieldLabel = { fontSize: 11, fontWeight: 600, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 };
  const fieldValue = { fontSize: 14, fontWeight: 500, color: 'var(--textPrimary)', wordBreak: 'break-word' };
  const inputBase = {
    width: '100%', padding: '10px 14px', borderRadius: 6, fontSize: 13,
    border: '1px solid var(--borderSecondary)', background: 'var(--bgPrimary)',
    color: 'var(--textPrimary)', boxSizing: 'border-box', outline: 'none',
    transition: 'border-color .2s',
  };
  const btnPrimary = (disabled) => ({
    padding: '10px 24px', borderRadius: 6, border: 'none', fontWeight: 600, fontSize: 13,
    background: disabled ? 'var(--bgTertiary)' : 'var(--accent)', color: disabled ? 'var(--textMuted)' : '#0f172a',
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all .2s',
  });
  const btnGhost = {
    padding: '10px 24px', borderRadius: 6, border: '1px solid var(--borderSecondary)',
    background: 'transparent', color: 'var(--textSecondary)', cursor: 'pointer', fontWeight: 500, fontSize: 13,
  };

  const SvgIcon = ({ d, size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
  );

  /* ─── Render ─── */
  return (
    <div style={PAGE}>
      <input ref={photoInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={handlePhotoUpload} />

      {/* ── Header ── */}
      <div style={{ ...HEADER, padding: mob ? '0 16px' : '0 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SvgIcon d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" size={20} />
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--textPrimary)', letterSpacing: '-0.3px' }}>Profile</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--textMuted)' }}>{currentUser?.email}</span>
          <span style={{
            padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
            background: currentUser?.role === 'admin' ? 'rgba(34,211,238,.12)' : 'rgba(59,130,246,.1)',
            color: currentUser?.role === 'admin' ? '#22d3ee' : '#60a5fa',
            border: `1px solid ${currentUser?.role === 'admin' ? 'rgba(34,211,238,.25)' : 'rgba(59,130,246,.2)'}`,
          }}>{currentUser?.role || 'operator'}</span>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ ...BODY, flexDirection: mob ? 'column' : 'row' }}>

        {/* Left Navigation */}
        <nav style={{
          flexShrink: 0, width: mob ? '100%' : 220,
          borderRight: mob ? 'none' : '1px solid var(--borderSecondary)',
          borderBottom: mob ? '1px solid var(--borderSecondary)' : 'none',
          background: 'var(--bgSecondary)', padding: mob ? '8px 8px' : '20px 10px',
          display: 'flex', flexDirection: mob ? 'row' : 'column', gap: mob ? 4 : 2, overflow: mob ? 'auto' : undefined,
        }}>
          {/* User card in nav */}
          {!mob && (
            <div style={{ padding: '14px 12px', marginBottom: 12, borderRadius: 8, background: 'var(--bgPrimary)', border: '1px solid var(--borderSecondary)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <AvatarDisplay avatarId={currentUser?.avatar} name={currentUser?.name} size={44} borderWidth={2} borderColor="var(--borderSecondary)" profilePhoto={currentUser?.profilePhoto} />
                  <button onClick={() => photoInputRef.current?.click()} disabled={photoUploading}
                    style={{
                      position: 'absolute', bottom: -2, right: -2, width: 20, height: 20, borderRadius: '50%',
                      background: 'var(--accent)', border: '2px solid var(--bgPrimary)',
                      display: 'grid', placeItems: 'center', cursor: 'pointer', padding: 0,
                    }}>
                    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth={2.5}><path d="M12 5v14M5 12h14" /></svg>
                  </button>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--textPrimary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.name || 'User'}</div>
                  <div style={{ fontSize: 11, color: 'var(--textMuted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.organizationName || 'UTMS'}</div>
                </div>
              </div>
            </div>
          )}
          {tabs.map(t => (
            <button key={t.key} onClick={() => { setActiveTab(t.key); setProfileMsg(null); setPwdMsg(null); }} style={navBtn(activeTab === t.key)}>
              <SvgIcon d={t.icon} size={16} />
              {(mob || true) && <span>{t.label}</span>}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          {!mob && (
            <>
              {currentUser?.profilePhoto && (
                <button onClick={handleRemovePhoto} disabled={photoUploading}
                  style={{ ...navBtn(false), color: 'var(--textMuted)', fontSize: 12, marginTop: 4 }}>
                  <SvgIcon d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" size={14} /> Remove Photo
                </button>
              )}
              <button onClick={logout} style={{ ...navBtn(false), color: '#f87171', marginTop: 8 }}>
                <SvgIcon d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" size={16} /> Sign Out
              </button>
            </>
          )}
        </nav>

        {/* Main Content */}
        <main style={{ flex: 1, overflow: 'auto', padding: mob ? '20px 16px 80px' : '28px 36px 40px' }}>
          <div style={{ maxWidth: 680 }}>
            <Toast msg={profileMsg} />

            {/* ═══ Overview ═══ */}
            {activeTab === 'details' && (
              <>
                {/* Info Grid */}
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: 'var(--textPrimary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SvgIcon d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" size={16} /> General Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: '16px 24px' }}>
                    {[
                      { label: 'Full Name', value: currentUser?.name },
                      { label: 'Email Address', value: currentUser?.email },
                      { label: 'Role', value: (currentUser?.role || 'operator').charAt(0).toUpperCase() + (currentUser?.role || 'operator').slice(1) },
                      { label: 'Organization', value: currentUser?.organizationName || 'UTMS' },
                      { label: 'Member Since', value: memberSince },
                      { label: 'Account ID', value: currentUser?.userId ? currentUser.userId.slice(0, 12) + '…' : '—' },
                    ].map(f => (
                      <div key={f.label} style={{ padding: '12px 16px', borderRadius: 8, background: 'var(--bgSecondary)', border: '1px solid var(--borderSecondary)' }}>
                        <div style={fieldLabel}>{f.label}</div>
                        <div style={fieldValue}>{f.value || '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: 'var(--textPrimary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SvgIcon d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" size={16} /> Activity Summary</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr 1fr' : '1fr 1fr 1fr', gap: 12 }}>
                    {[
                      { label: 'Drones', value: currentUser?.droneCount ?? 0, color: '#22d3ee', icon: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8' },
                      { label: 'Missions', value: currentUser?.missionCount ?? 0, color: '#3b82f6', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
                      { label: 'Access Level', value: (currentUser?.role || 'operator').toUpperCase(), color: '#f59e0b', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
                    ].map(s => (
                      <div key={s.label} style={{
                        padding: '16px 18px', borderRadius: 8, background: 'var(--bgSecondary)', border: '1px solid var(--borderSecondary)',
                        display: 'flex', alignItems: 'center', gap: 14,
                      }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${s.color}12`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                          <SvgIcon d={s.icon} size={18} />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{s.label}</div>
                          <div style={{ fontSize: typeof s.value === 'number' ? 22 : 14, fontWeight: 700, color: s.color, marginTop: 2 }}>{s.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div>
                  <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: 'var(--textPrimary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SvgIcon d="M13 10V3L4 14h7v7l9-11h-7z" size={16} /> Quick Actions</h3>
                  <div style={{ display: 'grid', gap: 1, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--borderSecondary)' }}>
                    {[
                      { label: 'Change Password', desc: 'Update your account password', action: 'password', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' },
                      { label: 'Upload Profile Photo', desc: 'JPG, PNG, GIF, WebP · Max 5 MB', action: 'photo', icon: 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z' },
                      { label: 'Edit Profile', desc: 'Name, email, and avatar', action: 'edit', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
                    ].map((r, i) => (
                      <button key={r.label}
                        onClick={() => r.action === 'photo' ? photoInputRef.current?.click() : setActiveTab(r.action)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', cursor: 'pointer',
                          background: 'var(--bgSecondary)', border: 'none', textAlign: 'left', transition: 'background .15s',
                        }}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--bgTertiary)', display: 'grid', placeItems: 'center', flexShrink: 0, color: 'var(--accent)' }}>
                          <SvgIcon d={r.icon} size={16} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--textPrimary)' }}>{r.label}</div>
                          <div style={{ fontSize: 11, color: 'var(--textMuted)', marginTop: 1 }}>{r.desc}</div>
                        </div>
                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--textMuted)" strokeWidth={2}><path d="M9 5l7 7-7 7" /></svg>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ═══ Edit Profile ═══ */}
            {activeTab === 'edit' && (
              <>
                <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600, color: 'var(--textPrimary)' }}>Edit Profile</h3>
                <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={fieldLabel}>Full Name</span>
                    <input value={editName} onChange={e => setEditName(e.target.value)} style={inputBase} placeholder="Your name" />
                  </label>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={fieldLabel}>Email Address</span>
                    <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} style={inputBase} placeholder="you@example.com" />
                  </label>
                </div>

                {/* Avatar Selection */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ ...fieldLabel, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Avatar</span>
                    <button onClick={() => setAvatarPickerOpen(!avatarPickerOpen)}
                      style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      {avatarPickerOpen ? 'Close' : 'Choose Avatar'}
                    </button>
                  </div>
                  {avatarPickerOpen && (
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${mob ? 54 : 64}px, 1fr))`, gap: 8, padding: 14, borderRadius: 8, background: 'var(--bgSecondary)', border: '1px solid var(--borderSecondary)' }}>
                      {AVATAR_OPTIONS.map(av => (
                        <button key={av.id} onClick={() => setEditAvatar(av.id)}
                          style={{
                            padding: 6, borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                            border: editAvatar === av.id ? '2px solid var(--accent)' : '1px solid var(--borderSecondary)',
                            background: editAvatar === av.id ? 'rgba(34,211,238,.06)' : 'transparent', transition: 'all .15s',
                          }}>
                          <AvatarDisplay avatarId={av.id} size={mob ? 28 : 32} />
                          <div style={{ fontSize: 9, color: 'var(--textMuted)', marginTop: 3 }}>{av.label}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={() => setActiveTab('details')} style={btnGhost}>Cancel</button>
                  <button onClick={handleProfileSave} disabled={profileSaving || !editName.trim() || !editEmail.trim()} style={btnPrimary(profileSaving || !editName.trim() || !editEmail.trim())}>
                    {profileSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}

            {/* ═══ Security ═══ */}
            {activeTab === 'password' && (
              <>
                <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600, color: 'var(--textPrimary)' }}>Change Password</h3>
                <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--textMuted)' }}>Password must be at least 6 characters long.</p>
                <Toast msg={pwdMsg} />
                <div style={{ display: 'grid', gap: 16, maxWidth: 400, marginBottom: 24 }}>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={fieldLabel}>Current Password</span>
                    <input type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} style={inputBase} />
                  </label>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={fieldLabel}>New Password</span>
                    <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} style={inputBase} />
                  </label>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={fieldLabel}>Confirm New Password</span>
                    <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                      style={{ ...inputBase, borderColor: confirmPwd && confirmPwd !== newPwd ? '#ef4444' : 'var(--borderSecondary)' }} />
                    {confirmPwd && confirmPwd !== newPwd && <span style={{ fontSize: 11, color: '#ef4444' }}>Passwords do not match</span>}
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', maxWidth: 400 }}>
                  <button onClick={() => setActiveTab('details')} style={btnGhost}>Cancel</button>
                  <button onClick={handlePasswordChange}
                    disabled={pwdSaving || !currentPwd || !newPwd || newPwd !== confirmPwd || newPwd.length < 6}
                    style={btnPrimary(pwdSaving || !currentPwd || !newPwd || newPwd !== confirmPwd || newPwd.length < 6)}>
                    {pwdSaving ? 'Updating…' : 'Update Password'}
                  </button>
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Mobile logout */}
      {mob && (
        <div style={{ flexShrink: 0, padding: '10px 16px', borderTop: '1px solid var(--borderSecondary)', background: 'var(--bgSecondary)' }}>
          <button onClick={logout} style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid rgba(239,68,68,.25)', background: 'rgba(239,68,68,.06)', color: '#f87171', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Sign Out</button>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
